import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from "react";
import { getAuthHeaders } from "../utils/authHeaders";
import { setAccessToken, clearAccessToken, refreshAccessToken } from "../utils/tokenStore";
import { API_URL } from "../config/api";

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  role: "employee" | "employer";
  isEmailVerified: boolean;
  onboardingCompleted: boolean;
  profileCompleted: boolean;
  cvUrl?: string;
  createdAt: string;
  updatedAt: string;
}

interface LoginResult {
  success: boolean;
  requiresRoleSelection?: boolean;
  roles?: string[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  register: (formData: Record<string, any>, role: "employee" | "employer") => Promise<void>;
  login: (email: string, password: string, role?: "employee" | "employer", rememberMe?: boolean) => Promise<LoginResult>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isLoggingOutRef = useRef(false);

  const register = async (formData: Record<string, any>, role: "employee" | "employer") => {
    try {
      setLoading(true);

      const response = await fetch(`${API_URL}/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include", // so token cookie is stored
        body: JSON.stringify({ ...formData, role }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Registration failed");
      }

      /* Held by tokenStore rather than written straight to localStorage:
         one place decides where the access token lives, and it is now a
         fifteen-minute value renewed from the httpOnly refresh cookie. */
      if (data.token) {
        setAccessToken(data.token);
      }
      // Map backend fields to frontend interface
      // Determine onboarding completion based on profile data presence
      const hasProfileData = data.profession || data.specialization || data.professionalAccreditation ||
                            data.uniName || data.contactPerson || data.address;

      const userData = {
        ...data,
        id: data._id || data.id,
        isEmailVerified: data.isVerified || data.isEmailVerified || false,
        onboardingCompleted: data.onboardingCompleted || hasProfileData || false,
        profileCompleted: data.profileCompleted || hasProfileData || false
      };
      setUser(userData);
    } catch (err) {
      const errorObj = err as Error;
      console.error("❌ Registration error details:", {
        message: errorObj.message,
        stack: errorObj.stack,
      });
      throw errorObj;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string, role?: "employee" | "employer", rememberMe?: boolean): Promise<LoginResult> => {
    try {
      setLoading(true);

      const response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({ email, password, role, rememberMe }),
      });

      const data = await response.json();

      // Handle multiple roles case (status 300)
      if (response.status === 300 && data.roles) {
        return {
          success: false,
          requiresRoleSelection: true,
          roles: data.roles
        };
      }

      if (!response.ok) {
        throw new Error(data.message || "Login failed");
      }

      /* Held by tokenStore rather than written straight to localStorage:
         one place decides where the access token lives, and it is now a
         fifteen-minute value renewed from the httpOnly refresh cookie. */
      if (data.token) {
        setAccessToken(data.token);
      }
      // Map backend fields to frontend interface
      // Determine onboarding completion based on profile data presence
      const hasProfileData = data.profession || data.specialization || data.professionalAccreditation ||
                            data.uniName || data.contactPerson || data.address;

      const userData = {
        ...data,
        id: data._id || data.id,
        isEmailVerified: data.isVerified || data.isEmailVerified || false,
        onboardingCompleted: data.onboardingCompleted || hasProfileData || false,
        profileCompleted: data.profileCompleted || hasProfileData || false
      };
      setUser(userData);
      return { success: true };
    } catch (err) {
      const errorObj = err as Error;
      console.error("❌ Login error details:", {
        message: errorObj.message,
        stack: errorObj.stack,
      });
      throw errorObj; // Let the calling component handle the error display
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      isLoggingOutRef.current = true;

      await fetch(`${API_URL}/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
      });

      // Clear user state regardless of response
      setUser(null);

      // Clear any stored data
      localStorage.removeItem('user-store');
      clearAccessToken();
      sessionStorage.clear();
    } catch (err) {
      console.error("❌ Logout error:", err);
      // Still clear user state even if logout request fails
      setUser(null);

      // Clear any stored data
      localStorage.removeItem('user-store');
      clearAccessToken();
      sessionStorage.clear();
    } finally {
      setLoading(false);
      // Reset logout flag after a short delay to prevent immediate re-authentication
      setTimeout(() => {
        isLoggingOutRef.current = false;
      }, 1000);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      // Skip authentication check if we're in the process of logging out
      if (isLoggingOutRef.current) {
        setLoading(false);
        return;
      }

      try {
        let res = await fetch(`${API_URL}/auth/me`, {
          headers: { ...getAuthHeaders() },
          credentials: "include",
        });

        /* The access token in hand is at most fifteen minutes old, so on any
           return visit it has almost certainly lapsed. A 401 here is the
           expected opening state, not a signed-out user: renew from the
           refresh cookie and ask again. This is what makes a session last
           thirty days without ever showing a login form. */
        if (res.status === 401) {
          const renewed = await refreshAccessToken();
          if (!renewed) throw new Error("Session not found");
          res = await fetch(`${API_URL}/auth/me`, {
            headers: { ...getAuthHeaders() },
            credentials: "include",
          });
        }

        if (!res.ok) throw new Error("Session not found");
        const data = await res.json();

        // Map backend fields to frontend interface
        // Determine onboarding completion based on profile data presence
        const hasProfileData = data.profession || data.specialization || data.professionalAccreditation ||
                              data.uniName || data.contactPerson || data.address;

        const userData = {
          ...data,
          id: data._id || data.id,
          isEmailVerified: data.isVerified || data.isEmailVerified || false,
          onboardingCompleted: data.onboardingCompleted || hasProfileData || false,
          profileCompleted: data.profileCompleted || hasProfileData || false
        };
        setUser(userData);
      } catch (err) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
    // Only run on mount — isLoggingOut is read via ref to avoid re-firing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateUser = (userData: Partial<User>) => {
    setUser(prev => prev ? { ...prev, ...userData } : null);
  };

  
  return (
    <AuthContext.Provider value={{ user, loading, register, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};